import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AnimateIn } from '../components/Layout';
import { profileData, languages, highlights, experienceTimeline, approach } from '../data/mock';

const About = () => {
  return (
    <div className="pt-20">
      {/* Page Hero */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.25em] uppercase mb-4 block">About</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#0B1F3A] leading-tight mb-6">
              The Man Behind<br />the Expertise
            </h1>
            <div className="w-16 h-[2px] bg-[#C9A646]" />
          </AnimateIn>
        </div>
      </section>

      {/* Profile Section */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            <AnimateIn>
              <div className="shrink-0">
                <div className="w-48 h-48 rounded-full bg-[#0B1F3A] flex items-center justify-center shadow-2xl overflow-hidden ring-4 ring-[#C9A646]/20">
                  {profileData.photo ? (
                    <img src={profileData.photo} alt={profileData.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[#C9A646] text-6xl font-heading font-bold">{profileData.initials}</span>
                  )}
                </div>
              </div>
            </AnimateIn>
            <AnimateIn delay={200}>
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A] mb-2">{profileData.name}</h2>
                <p className="text-[#C9A646] font-body font-semibold text-lg mb-6">{profileData.title} &bull; {profileData.subtitle}</p>
                <p className="text-gray-600 font-body text-lg leading-relaxed mb-4">{profileData.bio}</p>
                <p className="text-gray-500 font-body text-base leading-relaxed mb-8">{profileData.fullBio}</p>
                <Link to="/consultation">
                  <Button className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm px-8 h-11">
                    Schedule a Consultation <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </AnimateIn>
          </div>
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <div className="mb-16">
              <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase">Career</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A] mt-3">Experience Timeline</h2>
              <div className="w-16 h-[2px] bg-[#C9A646] mt-4" />
            </div>
          </AnimateIn>
          <div className="relative">
            <div className="absolute left-4 md:left-8 top-0 bottom-0 w-[2px] bg-[#C9A646]/20" />
            <div className="space-y-12">
              {experienceTimeline.map((item, idx) => (
                <AnimateIn key={idx} delay={idx * 150}>
                  <div className="relative pl-12 md:pl-20">
                    <div className="absolute left-[10px] md:left-[26px] top-2 w-3 h-3 rounded-full bg-[#C9A646] border-4 border-white shadow-sm" />
                    <span className="text-[#C9A646] font-heading font-bold text-lg">{item.period}</span>
                    <h3 className="font-heading text-xl font-bold text-[#0B1F3A] mt-1 mb-2">{item.title}</h3>
                    <p className="text-gray-500 font-body leading-relaxed">{item.description}</p>
                  </div>
                </AnimateIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Languages & Highlights */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <AnimateIn>
              <div>
                <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase">Communication</span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0B1F3A] mt-3 mb-8">Languages</h2>
                <div className="flex flex-wrap gap-3">
                  {languages.map((lang, idx) => (
                    <Badge key={idx} variant="outline" className="px-5 py-2.5 rounded-sm border-[#0B1F3A]/20 text-[#0B1F3A] font-body text-sm hover:border-[#C9A646] hover:bg-[#C9A646]/5 transition-colors cursor-default">
                      <span className="font-semibold">{lang.name}</span>
                      <span className="text-gray-400 ml-2">&bull; {lang.level}</span>
                    </Badge>
                  ))}
                </div>
              </div>
            </AnimateIn>
            <AnimateIn delay={200}>
              <div>
                <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase">Strengths</span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0B1F3A] mt-3 mb-8">Professional Highlights</h2>
                <div className="space-y-5">
                  {highlights.map((h, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="w-2 h-2 rounded-full bg-[#C9A646] mt-2.5 shrink-0" />
                      <div>
                        <h4 className="font-heading font-bold text-[#0B1F3A] mb-1">{h.title}</h4>
                        <p className="text-gray-500 font-body text-sm leading-relaxed">{h.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimateIn>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-24 bg-[#0B1F3A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimateIn>
            <div className="text-center mb-16">
              <span className="text-[#C9A646] font-body font-semibold text-sm tracking-[0.2em] uppercase">Process</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mt-3">My Approach</h2>
              <div className="w-16 h-[2px] bg-[#C9A646] mx-auto mt-4" />
            </div>
          </AnimateIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {approach.map((item, idx) => (
              <AnimateIn key={idx} delay={idx * 100}>
                <div className="text-center">
                  <span className="text-[#C9A646] text-4xl font-heading font-bold block mb-3">0{item.step}</span>
                  <h3 className="font-heading text-lg font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400 font-body text-sm leading-relaxed">{item.description}</p>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimateIn>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A] mb-6">Ready to Ensure Compliance?</h2>
            <p className="text-gray-500 font-body text-lg max-w-2xl mx-auto mb-8">
              Let's discuss your specific requirements and find the right compliance solution for your organization.
            </p>
            <Link to="/consultation">
              <Button className="bg-[#C9A646] hover:bg-[#B8953B] text-[#0B1F3A] font-semibold font-body rounded-sm px-8 h-12 text-base">
                Schedule Consultation
              </Button>
            </Link>
          </AnimateIn>
        </div>
      </section>
    </div>
  );
};

export default About;
